import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import '../styles/pages/BlogPage.css';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const blogPosts = [
    {
      id: 7,
      title: 'From Paper Chaos to Digital Clarity: Why Your Manual Systems Are Bleeding Money',
      excerpt: 'Your organization is leaking money—quietly. Paper files, spreadsheets, and WhatsApp approvals are costing you more than you realize.',
      category: 'Digital Transformation',
      date: 'Jan 20, 2026',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'from-paper-chaos-to-digital-clarity'
    },
    {
      id: 1,
      title: 'The Future of AI in Business Automation',
      excerpt: 'Discover how artificial intelligence is transforming the way businesses operate and compete in the digital age.',
      category: 'AI & Automation',
      date: 'Jan 5, 2026',
      readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'future-of-ai-business-automation'
    },
    {
      id: 2,
      title: 'Building Scalable Web Applications in 2026',
      excerpt: 'Learn the best practices and modern technologies for creating web applications that scale with your business growth.',
      category: 'Software Development',
      date: 'Dec 28, 2025',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'building-scalable-web-applications'
    },
    {
      id: 3,
      title: 'Digital Transformation: A Complete Guide for Nigerian Businesses',
      excerpt: 'Everything you need to know about successfully implementing digital transformation in your organization.',
      category: 'Digital Transformation',
      date: 'Dec 20, 2025',
      readTime: '10 min read',
      image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'digital-transformation-guide-nigerian-businesses'
    },
    {
      id: 4,
      title: 'Mobile-First Development: Why It Matters in Africa',
      excerpt: 'Understanding the importance of mobile-first approach in developing markets and how to implement it effectively.',
      category: 'Mobile Development',
      date: 'Dec 15, 2025',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'mobile-first-development-africa'
    },
    {
      id: 5,
      title: 'Cybersecurity Best Practices for Small Businesses',
      excerpt: 'Essential security measures every small business should implement to protect their digital assets.',
      category: 'Security',
      date: 'Dec 10, 2025',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'cybersecurity-best-practices'
    },
    {
      id: 6,
      title: 'The Rise of Fintech in Nigeria: Opportunities and Challenges',
      excerpt: 'Exploring the booming fintech ecosystem in Nigeria and what it means for entrepreneurs and developers.',
      category: 'Fintech',
      date: 'Dec 5, 2025',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'fintech-nigeria-opportunities'
    }
  ];

  const categories = [
    'All',
    'AI & Automation',
    'Software Development',
    'Digital Transformation',
    'Mobile Development',
    'Security',
    'Fintech'
  ];

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      paddingTop: '100px'
    }}>
      <SEO
        title="Blog - FIGO Digital Services | Tech Insights & Digital Transformation"
        description="Stay updated with the latest trends, insights, and best practices in digital transformation, AI, software development, and technology."
        keywords="tech blog Nigeria, AI automation, software development tips, digital transformation insights, fintech news, mobile development"
        url="https://figodigitalservices.com/blog"
      />

      {/* Hero Section */}
      <section className="blog-hero-section" style={{
        padding: '4rem 5%',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid #E2E8F0'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <h1 className="blog-hero-title" style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              marginBottom: '1.5rem',
              background: 'var(--gradient-1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: '700',
              lineHeight: '1.2'
            }}>
              Insights & Updates
            </h1>
            <p className="blog-hero-subtitle" style={{
              color: 'var(--text-secondary)',
              fontSize: '1.2rem',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Stay updated with the latest trends, insights, and best practices in digital transformation and technology.
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="blog-categories"
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '3rem',
            }}
          >
            {categories.map((category) => (
              <button
                key={category}
                className="blog-category-btn"
                onClick={() => setActiveCategory(category)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: activeCategory === category ? 'var(--gradient-1)' : '#ffffff',
                  border: `1px solid ${activeCategory === category ? 'transparent' : '#E2E8F0'}`,
                  borderRadius: '50px',
                  color: activeCategory === category ? '#ffffff' : 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="blog-grid-section" style={{
        padding: '6rem 5%',
        background: 'var(--bg-primary)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div
            className="blog-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '2.5rem'
            }}
          >
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="blog-card"
                style={{
                  background: '#ffffff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Post Image */}
                <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                  <div className="blog-card-image" style={{
                    height: '250px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={post.image}
                      alt={post.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </div>
                </Link>

                {/* Post Content */}
                <div className="blog-card-content" style={{ padding: '1.5rem' }}>
                  {/* Category & Date */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    <span className="blog-card-category" style={{
                      color: 'var(--figo-primary)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      fontWeight: '600'
                    }}>
                      {post.category}
                    </span>
                    <span className="blog-card-date" style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.8rem'
                    }}>
                      {post.date}
                    </span>
                  </div>

                  {/* Title */}
                  <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                    <h3 className="blog-card-title" style={{
                      fontSize: '1.35rem',
                      color: 'var(--text-primary)',
                      marginBottom: '0.75rem',
                      fontWeight: '700',
                      lineHeight: '1.4',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--figo-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                    >
                      {post.title}
                    </h3>
                  </Link>

                  {/* Excerpt */}
                  <p className="blog-card-excerpt" style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    marginBottom: '1.5rem'
                  }}>
                    {post.excerpt}
                  </p>

                  {/* Meta Info */}
                  <div className="blog-card-meta" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '1rem',
                    borderTop: '1px solid #E2E8F0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem'
                    }}>
                      <div className="blog-card-author-avatar" style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        background: 'var(--gradient-1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '5px'
                      }}>
                        <img
                          src="/figo-logo9.png"
                          alt="FIGO"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      <span className="blog-card-author-name" style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.85rem'
                      }}>
                        {post.author}
                      </span>
                    </div>
                    <span className="blog-card-readtime" style={{
                      color: 'var(--figo-primary)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {post.readTime}
                    </span>
                  </div>

                  {/* Read More Link */}
                  <div className="blog-card-readmore" style={{ marginTop: '1.25rem' }}>
                    <Link
                      to={`/blog/${post.slug}`}
                      style={{
                        color: 'var(--figo-primary)',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'gap 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.gap = '0.75rem';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.gap = '0.5rem';
                      }}
                    >
                      Read Article <span>→</span>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
